import {
  P2GIcon,
  SvgStarOutline,
  SvgStarFull,
  P2GTooltip,
  P2GProgress,
  SvgTickCircle,
  SvgAddCircle,
  SvgAddCircleBlock
} from '@parcel2go/core-ui';
import { useAnalytics, useTenant } from '@parcel2go/npm-p2g-app';
import { MouseEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { IP2GBasketSection } from '~/hooks/use-basket-section';
import { TranslationNamespaces } from '~/lib/constants';
import { QuoteAddress } from '~/lib/types/basket-types';
import { BasketSections } from '~/lib/types/enums';
import { useSavedAddressPersister } from '../../../hooks/use-saved-address-persister';

function FavouriteAddress({
  section,
  type
}: {
  section: IP2GBasketSection<QuoteAddress>;
  type: BasketSections.ORIGIN | BasketSections.DESTINATION;
}) {
  const { saveAddress } = useSavedAddressPersister();
  const [success, setSuccess] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [hovered, setHover] = useState<boolean>(false);
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const { user } = useAuth();
  const { tenant, settings } = useTenant();
  const { session, track } = useAnalytics();

  const onClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      setSaving(true);
      saveAddress(section.data, type === BasketSections.ORIGIN)?.then((r) => {
        setSaving(false);
        if (r?.ok) {
          setSuccess(true);
          track({ type: 'address_favourited', payload: { ...section.data } });
          setTimeout(() => {
            section.update({
              ...section.data,
              metadata: {
                ...section.data.metadata,
                type: 'SAVED',
                isDefaultSavedAddress: type === BasketSections.ORIGIN
              }
            });
          }, 2000);
        }
      });
    },
    [session, settings, tenant, user, section, track, type]
  );

  if (section.data.metadata?.type === 'SAVED') {
    return (
      <P2GTooltip
        title={t('ADDRESS_FAVOURITE_SAVED_TOOLTIP', {
          defaultValue: section.data.metadata?.isDefaultSavedAddress ? 'Default Address' : 'Saved'
        })}
      >
        {section.data.metadata?.isDefaultSavedAddress ? (
          <P2GIcon>
            <SvgStarFull />
          </P2GIcon>
        ) : (
          <P2GIcon palette="success">
            <SvgTickCircle />
          </P2GIcon>
        )}
      </P2GTooltip>
    );
  }

  if (
    !section.data.contactname ||
    !section.data.contactname.length ||
    !section.data.email ||
    !section.data.email.length
  ) {
    return null;
  }

  return (
    <P2GTooltip title={t('ADDRESS_FAVOURITE_TOOLTIP', { defaultValue: 'Save this address' })}>
      <span
        style={{ cursor: 'pointer' }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={onClick}
      >
        {saving ? (
          <P2GProgress size="small" />
        ) : type === BasketSections.ORIGIN ? (
          <P2GIcon palette={success ? 'success' : 'primary'}>
            {success ? <SvgTickCircle /> : hovered ? <SvgStarFull /> : <SvgStarOutline />}
          </P2GIcon>
        ) : (
          <P2GIcon palette={success ? 'success' : 'primary'}>
            {success ? <SvgTickCircle /> : hovered ? <SvgAddCircleBlock /> : <SvgAddCircle />}
          </P2GIcon>
        )}
      </span>
    </P2GTooltip>
  );
}

export default FavouriteAddress;
